import { motion } from "framer-motion";
import { Cloud, Shield, Footprints, Droplet, Star } from "lucide-react";
import { SmallCaps, EASE } from "@/components/bits";
import PreorderCta from "@/components/store/PreorderCta";
import { STORE } from "@/config/store";
import { LANDER_ANGLES } from "@/config/lander";

const MaskedLine = ({ children, delay, rotate = 0 }) => (
  <span className="block overflow-hidden px-1 -mx-1 py-0.5 -my-0.5">
    <motion.span
      className="block"
      initial={{ y: "112%", rotate: rotate * 2.2 }}
      animate={{ y: 0, rotate }}
      transition={{ duration: 0.85, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const PROOF_CHIPS = ["SLIP-RESISTANT", "CLOSED TOE", "EASY CLEAN"];

const FEATURES = [
  { id: "toe", icon: Shield, label: "Toe protection", bg: "bg-[#FF6BAA]", tilt: "-rotate-3" },
  { id: "grip", icon: Footprints, label: "Serious grip", bg: "bg-[#7CC9FF]", tilt: "rotate-2" },
  { id: "water", icon: Droplet, label: "Water resistant", bg: "bg-[#FFD84D]", tilt: "-rotate-2" },
];

// Compact benefit row below the photo — all four side by side, badge + tiny
// label, matching the tilted colour-badge look.
const HERO_BENEFITS = [
  { id: "comfort", icon: Cloud, label: "All-day comfort", bg: "bg-[#FF6BAA]", tilt: "-rotate-3" },
  { id: "toe", icon: Shield, label: "Toe protection", bg: "bg-[#FFD84D]", tilt: "rotate-2" },
  { id: "grip", icon: Footprints, label: "Serious grip", bg: "bg-[#7CC9FF]", tilt: "-rotate-2" },
  { id: "water", icon: Droplet, label: "Water resistant", bg: "bg-[#C6A2FF]", tilt: "rotate-3" },
];

// Desktop headline: plain ink type in the split layout.
const Headline = () => (
  <h1 className="font-display uppercase leading-[0.92] text-6xl">
    <span className="block">Work wear</span>
    <span className="block">
      shouldn't be <span className="text-[#FF6BAA]">ugly!</span>
    </span>
  </h1>
);

// Mobile headline: a three-line crescendo cascading down the locker column —
// the one clean text zone this photo has (heads fill everything else). Bigger
// type owns the whole left side instead of huddling in the corner: ivory
// lines, then the big pink UGLY! punch + sparkle, staggered tilts, hard
// shadow + halo over a dark patch so it never blends in.
const LockerHeadline = () => (
  <h1
    data-testid="store-hero-headline"
    className="absolute top-[84px] left-6 z-10 font-display uppercase leading-[0.92] pointer-events-none [filter:drop-shadow(2px_2px_0_rgba(17,17,17,0.9))_drop-shadow(0_0_18px_rgba(17,17,17,0.5))]"
  >
    <MaskedLine delay={0.25} rotate={-2}>
      <span className="text-ivory text-[11vw]">Work wear</span>
    </MaskedLine>
    <MaskedLine delay={0.38} rotate={1.5}>
      <span className="ml-2 text-ivory text-[9vw]">shouldn't be</span>
    </MaskedLine>
    <MaskedLine delay={0.5} rotate={-2.5}>
      <span className="text-[#FF6BAA] text-[15.5vw]">
        ugly!
        <svg
          viewBox="0 0 40 40"
          className="inline-block w-[5vw] h-[5vw] ml-1 -translate-y-0.5"
          fill="none"
          stroke="#FF6BAA"
          strokeWidth="5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6 26 L2 34" />
          <path d="M16 30 L16 39" />
          <path d="M26 26 L30 34" />
        </svg>
      </span>
    </MaskedLine>
  </h1>
);

// Vertical benefit bubbles down the left lane of the photo — under the
// headline, threaded through the measured gap between the sitting nurse
// (starts x~230 source) and her pink clogs (start y~1500 source). Tilts
// live on the circle span — framer-motion owns the wrapper's transform.
const BenefitBubbles = () => (
  <div
    data-testid="hero-benefit-bubbles"
    className="absolute left-4 top-[44%] z-10 flex flex-col gap-3"
  >
    {FEATURES.map((f, i) => (
      <motion.div
        key={f.id}
        data-testid={`hero-feature-${f.id}`}
        className="flex flex-col items-center w-14"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, delay: 0.65 + i * 0.08, ease: EASE }}
      >
        <span
          className={`w-9 h-9 rounded-full border-2 border-ink inline-flex items-center justify-center shadow-[2px_2px_0px_#111111] ${f.bg} ${f.tilt}`}
        >
          <f.icon className="w-4 h-4 text-ink" strokeWidth={2.2} />
        </span>
        <span className="mt-0.5 font-body font-bold text-[7px] tracking-[0.06em] uppercase leading-[1.15] text-center text-ivory [filter:drop-shadow(1px_1px_0_rgba(17,17,17,0.95))]">
          {f.label}
        </span>
      </motion.div>
    ))}
  </div>
);

const Sub = ({ a, desktop, className = "" }) => (
  <motion.p
    data-testid={desktop ? undefined : "store-hero-sub"}
    className={`text-[13px] leading-snug sm:text-lg sm:leading-normal max-w-md text-ink/80 font-medium ${className}`}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
  >
    {a.sub}
  </motion.p>
);

const ProofBlock = ({ desktop }) => (
  <motion.div
    data-testid={desktop ? undefined : "hero-proof-block"}
    className="mt-6 pb-10 sm:pb-0"
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.65, ease: EASE }}
  >
    <div className="flex flex-wrap gap-2">
      {PROOF_CHIPS.map((c) => (
        <span
          key={c}
          data-testid={desktop ? undefined : "hero-proof-slot"}
          className="font-display uppercase text-xs tracking-wide border-2 border-ink rounded-full px-3.5 py-1.5 bg-white"
        >
          {c}
        </span>
      ))}
    </div>
    <p className="mt-3 text-sm text-ink/60 max-w-md">
      Wear-testing with Australian healthcare workers is underway — real
      feedback lands here as the first production run ships.
    </p>
  </motion.div>
);

// Mobile hero: one continuous full-bleed photo straight under the header —
// headline stacked down the locker column, floor melts into ivory, and the
// yellow pill straddles the photo's bottom edge as the divider into the
// feature row. Desktop: full cozy frame in the split card.
export default function HeroLanding({ angle = "long-shift" }) {
  const a = LANDER_ANGLES[angle] || LANDER_ANGLES["long-shift"];

  return (
    <section data-testid="landing-hero" className="relative">
      {/* Mobile: single full-bleed scene — the photo tucks 48px up under
          the header so the pale ceiling band disappears and the lockers
          start right at the bar */}
      <div className="sm:hidden">
        <div className="relative -mt-12">
          <motion.img
            data-testid="store-hero-image"
            src="/images/hero-landing-mobile-900.webp?v=5"
            srcSet="/images/hero-landing-mobile-900.webp?v=5 900w, /images/hero-landing-mobile.webp?v=5 1280w"
            sizes="100vw"
            alt="Three nurses leaning on each other in a locker room wearing POSSO clogs in Pink, Light Blue and Yellow"
            className="w-full h-auto block select-none"
            width="900"
            height="1310"
            loading="eager"
            fetchPriority="high"
            draggable="false"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE }}
          />
          {/* dark patch behind the locker column so the type pops */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none bg-[radial-gradient(90%_45%_at_22%_22%,rgba(17,17,17,0.5),transparent_72%)]"
          />
          {/* floor melts into the ivory feature section */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-[5%] bg-gradient-to-b from-transparent via-ivory/60 to-ivory pointer-events-none"
          />
          <LockerHeadline />
          <BenefitBubbles />
          {/* the pill straddles the photo's bottom edge = the divider
              (translate on the outer div — framer owns inline transform) */}
          <div className="absolute inset-x-4 bottom-0 translate-y-1/2 z-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.7, ease: EASE }}
            >
              <PreorderCta
                testId="store-hero-cta"
                origin="landing_hero"
                variant="yellow"
                size="sm"
                label="First Drop Preorder — $119"
                className="w-full"
              />
            </motion.div>
          </div>
        </div>
        <div className="px-4 mt-8 max-w-2xl mx-auto w-full">
          <motion.div
            data-testid="hero-waitlist-proof"
            className="mt-10 flex items-center justify-center gap-1.5"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.78, ease: EASE }}
          >
            <span className="flex items-center gap-0.5" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-3.5 h-3.5 text-[#FFB800] fill-[#FFB800]"
                  strokeWidth={2}
                />
              ))}
            </span>
            <span className="text-xs font-semibold text-ink/70">
              1,000+ healthcare workers joined the waitlist
            </span>
          </motion.div>
          <div
            data-testid="hero-benefit-list"
            className="mt-5 grid grid-cols-4 gap-1"
          >
            {HERO_BENEFITS.map((b, i) => (
              <motion.div
                key={b.id}
                data-testid={`hero-benefit-${b.id}`}
                className="flex flex-col items-center text-center px-1"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.85 + i * 0.06, ease: EASE }}
              >
                <span
                  className={`w-10 h-10 rounded-full border-2 border-ink inline-flex items-center justify-center shadow-[2.5px_2.5px_0px_#111111] ${b.bg} ${b.tilt}`}
                >
                  <b.icon className="w-5 h-5 text-ink" strokeWidth={2.2} />
                </span>
                <span className="mt-1.5 font-body font-bold text-[9px] tracking-[0.08em] uppercase leading-tight text-ink/75">
                  {b.label}
                </span>
              </motion.div>
            ))}
          </div>
          <p className="mt-4 pb-8 text-xs text-ink/60 max-w-md mx-auto text-center">
            Wear-testing with Australian healthcare workers is underway — real
            feedback lands here as the first production run ships.
          </p>
        </div>
      </div>

      {/* Desktop: split hero — copy left, full photo right (framed card) */}
      <div className="hidden sm:grid sm:grid-cols-2 sm:min-h-[86vh] max-w-6xl mx-auto">
        <div className="flex flex-col justify-center px-8 py-14">
          <Headline />
          <Sub a={a} desktop className="mt-3" />
          <motion.div
            className="mt-5"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
          >
            <PreorderCta origin="landing_hero" className="w-full sm:max-w-sm" />
            <p className="mt-2">
              <SmallCaps>{STORE.heroReassurance}</SmallCaps>
            </p>
          </motion.div>
          <ProofBlock desktop />
        </div>
        <div className="relative flex items-center justify-center py-8 pr-4">
          <motion.img
            src="/images/hero-landing.webp?v=9"
            alt="Three nurses leaning on each other in a locker room wearing POSSO clogs in Pink, Light Blue and Yellow"
            className="max-h-[78vh] w-auto max-w-full rounded-3xl border-2 border-ink select-none shadow-[4px_4px_0px_#111111]"
            width="1200"
            height="2132"
            loading="lazy"
            draggable="false"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          />
        </div>
      </div>
    </section>
  );
}
